import { parseTypeName } from './resource';
import { createDirectedGraph } from 'directed-graph';
import { create } from 'event-emitter';
const wrapHandler = (handler, selfIsParent) => handler && (opts => handler({
  dataChanged: opts.dataChanged,
  flagsChanged: opts.flagsChanged,
  self: selfIsParent ? opts.parent : opts.child,
  other: selfIsParent ? opts.child : opts.parent
}));
export const resolveEdges = link => ({
  push: {
    linkKey: link.key,
    behavior: link.pushBehavior,
    resolve: wrapHandler(link.onPush, true)
  },
  pull: {
    linkKey: link.key,
    behavior: link.pullBehavior,
    resolve: wrapHandler(link.onPull, false)
  }
});
export const createResourceRegistry = () => {
  const registry = createDirectedGraph();
  const emitter = create();
  let pendingAdded = [];
  let pendingRemoved = [];
  let pendingTypeNames = new Set();
  let batchScheduled = false;
  const scheduleSchemaChange = (added, removed, typeName) => {
    if (added) pendingAdded.push(...added);
    if (removed) pendingRemoved.push(...removed);
    pendingTypeNames.add(typeName);
    if (!batchScheduled) {
      batchScheduled = true;
      queueMicrotask(() => {
        const a = pendingAdded;
        const r = pendingRemoved;
        const types = pendingTypeNames;
        pendingAdded = [];
        pendingRemoved = [];
        pendingTypeNames = new Set();
        batchScheduled = false;
        emitter.emit('schemaChange', {
          added: a.length > 0 ? a : null,
          removed: r.length > 0 ? r : null,
          registeredTypeNames: types
        });
      });
    }
  };
  const linksByKey = new Map();
  const visitLinks = (typeName, visitor) => {
    const outgoing = registry.edge.getOutgoing(typeName);
    if (outgoing) {
      for (const neighbor of outgoing) {
        visitor(registry.edge.metadata.get(typeName, neighbor));
      }
    }
  };
  const addLink = (registeringResource, link) => {
    var _link$tagParams, _link$tagData, _link$tagParams2, _link$tagData2;
    const {
      selfTypeName,
      otherTypeName
    } = link;
    const {
      push,
      pull
    } = resolveEdges(link);
    linksByKey.set(link.key, link);
    registry.edge.add(selfTypeName, otherTypeName, {
      link,
      registeringResource,
      tags: (_link$tagParams = link.tagParams) === null || _link$tagParams === void 0 ? void 0 : _link$tagParams.self,
      tagData: (_link$tagData = link.tagData) === null || _link$tagData === void 0 ? void 0 : _link$tagData.self,
      outgoing: push,
      incoming: pull
    });
    registry.edge.add(otherTypeName, selfTypeName, {
      link,
      registeringResource,
      tags: (_link$tagParams2 = link.tagParams) === null || _link$tagParams2 === void 0 ? void 0 : _link$tagParams2.other,
      tagData: (_link$tagData2 = link.tagData) === null || _link$tagData2 === void 0 ? void 0 : _link$tagData2.other,
      outgoing: pull,
      incoming: push
    });
  };
  const removeLink = link => {
    const {
      selfTypeName,
      otherTypeName
    } = link;
    linksByKey.delete(link.key);
    registry.edge.remove(selfTypeName, otherTypeName);
    registry.edge.remove(otherTypeName, selfTypeName);
  };
  const registerResource = (resource, links = []) => {
    const priorLinks = [];
    visitLinks(resource.typeName, ({
      registeringResource,
      link
    }) => {
      if (registeringResource === resource.typeName) {
        priorLinks.push(link);
      }
    });
    registry.node.set(resource.typeName, resource);
    const newKeys = new Set(links.map(l => l.key));
    const priorKeys = new Set(priorLinks.map(l => l.key));
    let removed = null;
    for (const prior of priorLinks) {
      if (!newKeys.has(prior.key)) {
        var _removed;
        removeLink(prior);
        ((_removed = removed) !== null && _removed !== void 0 ? _removed : removed = []).push(prior);
      }
    }
    let added = null;
    for (const link of links) {
      addLink(resource.typeName, link);
      if (!priorKeys.has(link.key)) {
        var _added;
        ((_added = added) !== null && _added !== void 0 ? _added : added = []).push(link);
      }
    }
    if (added || removed || priorLinks.length > 0) {
      scheduleSchemaChange(added, removed, resource.typeName);
    }
  };
  const getResourceFromTypeName = typeName => {
    const def = registry.node.get(typeName);
    if (!def) {
      throw new Error(`Resource ${typeName} not found in registry`);
    }
    return def;
  };
  return {
    emitter,
    graph: registry,
    getLinkInfo: (selfTypeName, otherTypeName) => registry.edge.metadata.get(selfTypeName, otherTypeName),
    forEachLink: visitLinks,
    getResourceFromTypeName,
    getResource: instanceKey => getResourceFromTypeName(parseTypeName(instanceKey)),
    getLinkByKey: key => linksByKey.get(key),
    registerResource
  };
};
export const defaultResourceRegistry = createResourceRegistry();